import React, { useEffect } from 'react';
import { AuthProvider, useAuth, hasAuthParams } from 'react-oidc-context';
import type { AuthProviderProps } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import Spinner from '@/components/ui/spinner';
import { useAuthUser } from '@/lib/hooks/useAuthUser';
import { OIDC_ISSUER, OIDC_CLIENT_ID, OIDC_REDIRECT_URI, OIDC_LOGOUT_RETURN_TO } from '@/lib/env';

const PUBLIC_ROUTES = new Set(['/login', '/auth/callback', '/signout']);

function AuthGuard({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location.pathname;
  const isPublicRoute = PUBLIC_ROUTES.has(pathname);
  const isProfileRoute = pathname === '/profile' || pathname.startsWith('/profile/');

  const { emiratesId } = useAuthUser();
  const hasEmiratesId = Boolean(emiratesId);
  const requiresProfileRedirect =
    auth.isAuthenticated && !auth.isLoading && !isPublicRoute && !isProfileRoute && !hasEmiratesId;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1. Sync accessToken to localStorage
    if (auth.isAuthenticated && auth.user?.access_token) {
      localStorage.setItem('accessToken', auth.user.access_token);
    } else if (!auth.isAuthenticated && !auth.isLoading) {
      localStorage.removeItem('accessToken');
    }

    // 2. Automatically trigger signinRedirect if on a protected route while unauthenticated
    if (!isPublicRoute && !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator && !hasAuthParams()) {
      void auth.signinRedirect();
    }

    // 3. Always redirect logged in user to profile page in case he does not have emirates id in his profile
    if (requiresProfileRedirect) {
      navigate('/profile', { replace: true });
    }
  }, [auth, isPublicRoute, requiresProfileRedirect, navigate]);

  // Show a full-page spinner while checking session, performing auth redirect, or redirecting to profile
  if (!isPublicRoute && (auth.isLoading || !auth.isAuthenticated || requiresProfileRedirect)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return <>{children}</>;
}

const oidcConfig: AuthProviderProps = {
  authority: OIDC_ISSUER,
  client_id: OIDC_CLIENT_ID,
  redirect_uri: OIDC_REDIRECT_URI,
  post_logout_redirect_uri: OIDC_LOGOUT_RETURN_TO,
  response_type: 'code',
  scope: 'openid profile IdentityServerApi',
  onSigninCallback: () => {
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  },
};

export default function OidcAuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider {...oidcConfig}>
      <AuthGuard>{children}</AuthGuard>
    </AuthProvider>
  );
}
